import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Polygon, Circle, CircleMarker, Popup, LayersControl } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Car, Footprints, Bike, ArrowLeft, MapPin, Bus, Car as CarIcon, Loader2, ExternalLink, Star, Play, ParkingCircle } from 'lucide-react';
import PlaceInsights from '@/components/PlaceInsights';
import FloorPlanEditor from '@/components/FloorPlanEditor';
import 'leaflet/dist/leaflet.css';

const MODE_META = {
  drive: { label: 'Auto', icon: Car, color: '#1e3a8a' },
  walk:  { label: 'Caminando', icon: Footprints, color: '#15803d' },
  bike:  { label: 'Bicicleta', icon: Bike, color: '#b45309' },
};

// Time → fill opacity (shorter time = more intense)
const TIME_COLORS = {
  drive: { 5: '#1d4ed8', 10: '#2563eb', 15: '#3b82f6', 20: '#60a5fa', 30: '#93c5fd' },
  walk:  { 5: '#14532d', 10: '#166534', 15: '#15803d', 20: '#22c55e', 30: '#86efac' },
  bike:  { 5: '#78350f', 10: '#92400e', 15: '#b45309', 20: '#d97706', 30: '#fbbf24' },
};

const RADII_KM = [0.5, 1, 3, 5, 10];

export default function PlaceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [place, setPlace] = useState(null);
  const [iso, setIso] = useState(null);
  const [mob, setMob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingIso, setLoadingIso] = useState(false);
  const [loadingMob, setLoadingMob] = useState(false);
  const [modes, setModes] = useState({ drive: true, walk: true, bike: false });
  const [times, setTimes] = useState({ 5: true, 10: true, 15: true, 20: false, 30: false });
  const [radius, setRadius] = useState(1);
  const [showRadius, setShowRadius] = useState(true);

  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState('');
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [pRes, profRes] = await Promise.all([
          api.get(`/places/${id}`),
          api.get('/business-profiles'),
        ]);
        setPlace(pRes.data);
        setProfiles(profRes.data || []);
        if (profRes.data?.length) setProfileId(profRes.data[0].id);
      } catch (e) {
        toast.error('No se pudo cargar el lugar');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const loadIsochrones = async () => {
    if (!place?.lat || !place?.lon) return;
    setLoadingIso(true);
    try {
      const r = await api.get(`/places/${id}/isochrones`, {
        params: { lat: place.lat, lon: place.lon, modes: 'drive,walk,bike', minutes: '5,10,15,20,30' },
      });
      setIso(r.data);
      toast.success('Isócronas generadas');
    } catch { toast.error('Error generando isócronas'); }
    finally { setLoadingIso(false); }
  };

  const loadMobility = async () => {
    if (!place?.lat || !place?.lon) return;
    setLoadingMob(true);
    try {
      const r = await api.get(`/places/${id}/mobility`, { params: { lat: place.lat, lon: place.lon, radius_m: 800 } });
      setMob(r.data);
    } catch { toast.error('Error obteniendo movilidad'); }
    finally { setLoadingMob(false); }
  };

  useEffect(() => {
    if (place?.lat) {
      loadIsochrones();
      loadMobility();
    }
  }, [place?.lat, place?.lon, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const launchAnalysis = async () => {
    if (!profileId) return toast.error('Selecciona un perfil de negocio');
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return;
    setLaunching(true);
    try {
      const r = await api.post('/analyses/point', {
        place_id: id,
        lat: place.lat,
        lon: place.lon,
        place_name: place.name,
        place_type: place.type_detected,
        radius_km: radius,
        business_profile: profile,
        name: `Punto — ${place.name} (${radius} km)`,
      });
      toast.success('Análisis lanzado — redirigiendo…');
      nav(`/analyses/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error lanzando análisis');
    } finally { setLaunching(false); }
  };

  if (loading) return <div className="p-12 text-slate-500">Cargando…</div>;
  if (!place) return <div className="p-12 text-slate-500">Lugar no encontrado</div>;

  const center = [place.lat || 19.4326, place.lon || -99.1332];

  // Build isochrone polygons filtered by active toggles, ordered largest to smallest so smaller shows on top
  const activeModes = Object.keys(modes).filter(m => modes[m]);
  const activeTimes = Object.keys(times).filter(t => times[t]).map(Number).sort((a, b) => b - a);

  return (
    <div className="p-6 md:p-10 max-w-[1600px]">
      <button onClick={() => nav('/search')} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-search">
        <ArrowLeft size={14} /> Volver al buscador
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Left column: Place info + Mobility + Analysis launcher */}
        <div className="space-y-4">
          {place.photo_url && <img src={place.photo_url} alt={place.name} className="w-full h-48 object-cover border border-slate-200" />}

          <div className="bg-white border border-slate-200 p-5" data-testid="place-info-card">
            <div className="label-caps flex items-center gap-1"><MapPin size={12} /> {place.type_detected || 'Ubicación'}</div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{place.name}</h1>
            {place.address && <div className="text-sm text-slate-600 mt-1">{place.address}</div>}
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
              {place.rating && <span className="flex items-center gap-1"><Star size={12} className="text-amber-500" fill="currentColor" /> {place.rating.toFixed(1)}</span>}
              {place.reviews != null && <span>{place.reviews.toLocaleString('es-MX')} reseñas</span>}
              {place.price_level && <span>{'$'.repeat(Math.min(4, {"PRICE_LEVEL_INEXPENSIVE":1,"PRICE_LEVEL_MODERATE":2,"PRICE_LEVEL_EXPENSIVE":3,"PRICE_LEVEL_VERY_EXPENSIVE":4}[place.price_level] || 1))}</span>}
            </div>
            {place.website && <a href={place.website} target="_blank" rel="noreferrer" className="text-xs text-blue-900 hover:underline flex items-center gap-1 mt-3"><ExternalLink size={11} />{place.website}</a>}
            <div className="text-xs text-slate-400 mt-2">{place.lat?.toFixed(6)}, {place.lon?.toFixed(6)}</div>
          </div>

          {/* Mobility KPIs */}
          <div className="bg-white border border-slate-200 p-5" data-testid="mobility-card">
            <div className="label-caps mb-3">Movilidad y accesibilidad</div>
            {loadingMob ? (
              <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={14} className="animate-spin" /> Analizando…</div>
            ) : mob ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <MobKpi label="Global" value={mob.overall.score} category={mob.overall.category} />
                  <MobKpi label="Transporte" value={mob.transit.score.score} category={mob.transit.score.category} />
                  <MobKpi label="Estac." value={mob.parking.score.score} category={mob.parking.score.category} />
                </div>
                <div className="text-xs text-slate-600 mb-3">{mob.overall.label}</div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Bus size={14} className="text-slate-500" />
                    <span><strong>{mob.transit.count}</strong> paradas/estaciones en 800m</span>
                    {mob.transit.nearest_m != null && <span className="text-slate-400 ml-auto">más cercana: {mob.transit.nearest_m}m</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <ParkingCircle size={14} className="text-slate-500" />
                    <span><strong>{mob.parking.count}</strong> estacionamientos en 500m</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-400">Sin datos.</div>
            )}
          </div>

          {/* Analysis launcher */}
          <div className="bg-blue-900 text-white p-5" data-testid="analyze-launcher">
            <div className="label-caps text-white/70 mb-2">Análisis territorial</div>
            <div className="text-sm mb-3">Lanza un análisis completo para este punto usando un perfil de negocio.</div>

            <label className="text-xs text-white/70 block mb-1">Radio de influencia</label>
            <div className="flex gap-1 mb-3">
              {RADII_KM.map(r => (
                <button key={r} onClick={() => setRadius(r)} data-testid={`radius-${r}`}
                  className={`flex-1 text-xs py-1.5 border transition-colors ${radius === r ? 'bg-white text-blue-900 border-white font-semibold' : 'bg-transparent text-white/80 border-white/30 hover:bg-white/10'}`}>
                  {r} km
                </button>
              ))}
            </div>

            <label className="text-xs text-white/70 block mb-1">Perfil de negocio</label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger data-testid="profile-select" className="bg-white text-slate-900 text-xs mb-3">
                <SelectValue placeholder={profiles.length ? 'Selecciona un perfil' : 'Crea primero un perfil'} />
              </SelectTrigger>
              <SelectContent>
                {profiles.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.project_name || p.company_name} — {p.industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              data-testid="launch-analysis-btn"
              disabled={!profileId || launching}
              onClick={launchAnalysis}
              className="w-full bg-white text-blue-900 hover:bg-slate-100 font-semibold"
            >
              {launching ? <><Loader2 size={14} className="animate-spin mr-2" />Lanzando…</> : <><Play size={14} className="mr-2" />Iniciar análisis</>}
            </Button>
            {!profiles.length && (
              <a href="/profiles" className="block text-xs text-white/70 underline mt-2">Crear un perfil primero →</a>
            )}
          </div>

          {/* Sprint 6: Floor plan editor (only for plazas) */}
          {['plaza_comercial', 'strip_mall', 'ancla_retail'].includes(place.type_detected) && (
            <FloorPlanEditor placeId={id} placeName={place.name} />
          )}

          {/* Sprint 8: Field checklist */}
          <Button onClick={() => nav(`/field?place_id=${id}&place_name=${encodeURIComponent(place.name)}`)}
            data-testid="open-field-checklist" variant="outline" className="w-full">
            📋 Checklist de campo (mobile)
          </Button>
        </div>

        {/* Right column: Map + controls */}
        <div>
          {/* Toggle controls */}
          <div className="bg-white border border-slate-200 p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="label-caps mb-2">Modos</div>
                <div className="flex gap-1">
                  {Object.entries(MODE_META).map(([m, meta]) => {
                    const Icon = meta.icon;
                    const active = modes[m];
                    return (
                      <button key={m} onClick={() => setModes({ ...modes, [m]: !active })} data-testid={`mode-${m}`}
                        className={`flex-1 flex items-center justify-center gap-1 text-xs py-2 border transition-colors ${active ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                        style={active ? { backgroundColor: meta.color } : {}}>
                        <Icon size={13} /> {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="label-caps mb-2">Tiempo (min)</div>
                <div className="flex gap-1">
                  {[5, 10, 15, 20, 30].map(t => (
                    <button key={t} onClick={() => setTimes({ ...times, [t]: !times[t] })} data-testid={`time-${t}`}
                      className={`flex-1 text-xs py-2 border transition-colors ${times[t] ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-caps mb-2">Radios de influencia</div>
                <div className="flex gap-1">
                  <button onClick={() => setShowRadius(!showRadius)} data-testid="toggle-radius"
                    className={`flex-1 text-xs py-2 border transition-colors ${showRadius ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                    {showRadius ? `Ocultar ${radius} km` : `Mostrar ${radius} km`}
                  </button>
                </div>
              </div>
            </div>

            {iso && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>
                  Método: <strong className="text-slate-700">{iso.method}</strong>
                  {activeModes.map(m => {
                    const info = iso.modes?.[m];
                    if (!info) return null;
                    return <span key={m} className="ml-3">{info.label}: {info.effective_speed_kmh} km/h{info.calibrated ? ' (calibrado)' : ''}</span>;
                  })}
                </span>
                {loadingIso && <Loader2 size={12} className="animate-spin" />}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="border border-slate-200 overflow-hidden" style={{ height: 640 }}>
            <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom data-testid="place-map">
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Estándar">
                  <TileLayer attribution='&copy; OSM' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Claro">
                  <TileLayer attribution='&copy; Carto' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>
              </LayersControl>

              {/* Isochrone polygons: largest to smallest so smallest sits on top */}
              {iso && activeModes.map(mode => (
                activeTimes.map(t => {
                  const isoData = iso.modes?.[mode]?.isochrones?.find(x => x.minutes === t);
                  if (!isoData) return null;
                  const color = TIME_COLORS[mode]?.[t] || MODE_META[mode].color;
                  return (
                    <Polygon
                      key={`${mode}-${t}`}
                      positions={isoData.polygon}
                      pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 1.5 }}
                    >
                      <Popup>
                        <strong>{MODE_META[mode].label} · {t} min</strong><br />
                        Radio estimado: {isoData.radius_km} km
                      </Popup>
                    </Polygon>
                  );
                })
              ))}

              {/* Influence radius circle */}
              {showRadius && (
                <Circle center={center} radius={radius * 1000} pathOptions={{ color: '#0f172a', fillOpacity: 0.03, weight: 2, dashArray: '6 6' }} />
              )}

              {/* Transit stations */}
              {mob?.transit?.stations?.map((s, i) => s.lat && (
                <CircleMarker key={`t-${i}`} center={[s.lat, s.lon]} radius={5} pathOptions={{ color: '#7c3aed', fillColor: '#a78bfa', fillOpacity: 0.85 }}>
                  <Popup><strong>{s.name}</strong><br />{s.primary_type}<br />{s.distance_m}m del punto</Popup>
                </CircleMarker>
              ))}

              {/* Parking */}
              {mob?.parking?.spots?.map((s, i) => s.lat && (
                <CircleMarker key={`p-${i}`} center={[s.lat, s.lon]} radius={4} pathOptions={{ color: '#0f766e', fillColor: '#5eead4', fillOpacity: 0.8 }}>
                  <Popup><strong>{s.name}</strong><br />Estacionamiento · {s.distance_m}m</Popup>
                </CircleMarker>
              ))}

              {/* Origin marker */}
              <Marker position={center}>
                <Popup>
                  <strong>{place.name}</strong><br />{place.address}
                </Popup>
              </Marker>
            </MapContainer>
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#a78bfa' }} /> Transporte público</div>
            <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#5eead4' }} /> Estacionamientos</div>
            {activeModes.map(m => (
              <div key={m} className="flex items-center gap-1">
                <span className="w-3 h-3" style={{ backgroundColor: MODE_META[m].color, opacity: 0.5 }} /> {MODE_META[m].label}
              </div>
            ))}
            <div className="ml-auto text-slate-400 italic">Isócronas estimadas · calibradas con Google Routes</div>
          </div>
        </div>
      </div>

      {/* Sprint 3 + 4: Insights (Popularidad, Foot-traffic, Directorio, Índices) */}
      <PlaceInsights
        placeId={id}
        lat={place.lat}
        lon={place.lon}
        industry={profiles.find(p => p.id === profileId)?.industry || ''}
        isPlaza={['plaza_comercial', 'strip_mall', 'ancla_retail'].includes(place.type_detected)}
      />
    </div>
  );
}

const catColor = {
  excelente: 'text-green-800 bg-green-50',
  buena: 'text-green-700 bg-green-50',
  moderada: 'text-amber-700 bg-amber-50',
  limitada: 'text-orange-700 bg-orange-50',
  muy_limitada: 'text-red-700 bg-red-50',
  abundante: 'text-green-800 bg-green-50',
  escasa: 'text-red-700 bg-red-50',
};

const MobKpi = ({ label, value, category }) => (
  <div className={`p-2 ${catColor[category] || 'bg-slate-50 text-slate-700'}`}>
    <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
    <div className="font-display text-xl font-bold">{value}</div>
  </div>
);
